import { createElement, Fragment, type ReactNode } from "react";
import { en, type MessageKey, type Messages } from "./en";

export type { MessageKey, Messages } from "./en";

/**
 * Словари по языкам. Новый язык — файл es.ts с `export const es: Messages = {...}`
 * и строка здесь; ни один экран при этом не меняется.
 */
const catalogs = { en } satisfies Record<string, Messages>;

export type Locale = keyof typeof catalogs;

/** BCP-47 для Intl: форматы чисел, валют и дат идут за языком интерфейса. */
const INTL_LOCALES: Record<Locale, string> = { en: "en-US" };

let current: Locale = "en";

export function setLocale(locale: Locale): void {
  current = locale;
}

export function currentLocale(): Locale {
  return current;
}

export function intlLocale(): string {
  return INTL_LOCALES[current];
}

export type Params = Readonly<Record<string, string | number>>;

/** Подстановка {name}; неизвестный плейсхолдер остаётся как есть — так ошибку видно. */
export function interpolate(template: string, params: Params = {}): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : whole,
  );
}

export function t(key: MessageKey, params?: Params): string {
  const catalog: Messages = catalogs[current];
  return interpolate(catalog[key] ?? en[key], params);
}

/** Основы ключей с множественным числом: "hero.episodesCount" для "hero.episodesCount.other". */
export type PluralBase = {
  [K in MessageKey]: K extends `${infer Base}.other` ? Base : never;
}[MessageKey];

const pluralRules = new Map<string, Intl.PluralRules>();

function pluralCategory(count: number): Intl.LDMLPluralRule {
  const locale = intlLocale();
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRules.set(locale, rules);
  }
  return rules.select(count);
}

function isMessageKey(key: string): key is MessageKey {
  return Object.prototype.hasOwnProperty.call(en, key);
}

/** Строка с числом: форма выбирается правилами языка, {count} подставляется сам. */
export function tn(base: PluralBase, count: number, params: Params = {}): string {
  const exact = `${base}.${pluralCategory(count)}`;
  const key: MessageKey = isMessageKey(exact) ? exact : (`${base}.other` as MessageKey);
  return t(key, { count, ...params });
}

/**
 * Строка со вставками-элементами (ссылки посреди фразы). Порядок слов в языках разный,
 * поэтому ссылка — плейсхолдер в переводе, а не склейка трёх кусков в коде.
 */
export function tNodes(key: MessageKey, nodes: Readonly<Record<string, ReactNode>>, params: Params = {}): ReactNode[] {
  const text = t(key, params);
  const parts: ReactNode[] = [];
  const pattern = /\{(\w+)\}/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    const name = match[1] ?? "";
    if (!Object.prototype.hasOwnProperty.call(nodes, name)) continue;
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(createElement(Fragment, { key: `${name}-${match.index}` }, nodes[name]));
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
